import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import TripDetail from "@/components/TripDetail";
import {
  Loader2, Plus, RefreshCw, Trash2, Workflow, User, CalendarRange,
  Search, Filter, Archive, ListChecks, X,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ALL_AGENTS = "__all__";

export default function TripsPanel() {
  const [view, setView] = useState("active"); // active | archived
  const [activeTrips, setActiveTrips] = useState([]);
  const [archivedTrips, setArchivedTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [sofiTripId, setSofiTripId] = useState("");
  const [kimkimUrl, setKimkimUrl] = useState("");
  const [selected, setSelected] = useState(null);
  const [scrapeStatus, setScrapeStatus] = useState(null); // {busy, trip, queued}

  // Filtros
  const [agentFilter, setAgentFilter] = useState(ALL_AGENTS);
  const [monthFilter, setMonthFilter] = useState("");
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, z] = await Promise.all([
        axios.get(`${API}/trips`, { params: { archived: false } }),
        axios.get(`${API}/trips`, { params: { archived: true } }),
      ]);
      setActiveTrips(a.data);
      setArchivedTrips(z.data);
    } catch {
      toast.error("No se pudieron cargar los itinerarios");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Polling del estado del scraper (5s) para mostrar el banner cuando otro
  // agente esté procesando un viaje.  Serializar los scrapes evita que dos
  // navegadores Playwright se pisen las cookies de SOFI/kimkim.
  useEffect(() => {
    let mounted = true;
    const tick = async () => {
      try {
        const { data } = await axios.get(`${API}/trips/scrape-status`);
        if (mounted) setScrapeStatus(data);
      } catch { /* silencio */ }
    };
    tick();
    const id = setInterval(tick, 5000);
    return () => { mounted = false; clearInterval(id); };
  }, []);

  const importTrip = useCallback(async (e) => {
    e?.preventDefault?.();
    if (!sofiTripId.trim() || !kimkimUrl.trim()) return;
    setImporting(true);
    try {
      // Devuelve inmediatamente con status="processing". El detalle hace polling
      // hasta que llegue a "ready" o "error".
      const { data } = await axios.post(
        `${API}/trips/import`,
        { sofi_trip_id: sofiTripId.trim(), kimkim_trip_url: kimkimUrl.trim() },
        { timeout: 30000 }
      );
      toast.success("Importación lanzada — abriendo el detalle mientras se procesa");
      setSofiTripId(""); setKimkimUrl("");
      await load();
      setSelected(data.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error lanzando la importación");
    } finally {
      setImporting(false);
    }
  }, [sofiTripId, kimkimUrl, load]);

  const removeTrip = useCallback(async (trip) => {
    if (!window.confirm(`¿Borrar el viaje de ${trip.client_name || trip.sofi_trip_id}? Esta acción no se puede deshacer.`)) return;
    try {
      await axios.delete(`${API}/trips/${trip.id}`);
      toast.success("Viaje eliminado");
      if (selected === trip.id) setSelected(null);
      await load();
    } catch {
      toast.error("No se pudo borrar el viaje");
    }
  }, [load, selected]);

  const retryTrip = useCallback(async (trip) => {
    try {
      await axios.post(`${API}/trips/${trip.id}/reanalyze`);
      toast.success("Reintentando importación…");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo reintentar la importación");
    }
  }, [load]);

  // Lista visible según pestaña + filtros aplicados en frontend
  const baseList = view === "active" ? activeTrips : archivedTrips;
  const allAgents = useMemo(() => {
    const set = new Set();
    [...activeTrips, ...archivedTrips].forEach((t) => t.created_by && set.add(t.created_by));
    return Array.from(set).sort();
  }, [activeTrips, archivedTrips]);

  const visibleTrips = useMemo(() => {
    return baseList.filter((t) => {
      if (agentFilter !== ALL_AGENTS && t.created_by !== agentFilter) return false;
      if (monthFilter && !(t.start_iso || "").startsWith(monthFilter)) return false;
      if (search && !(t.client_name || "").toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [baseList, agentFilter, monthFilter, search]);

  const clearFilters = () => { setAgentFilter(ALL_AGENTS); setMonthFilter(""); setSearch(""); };
  const filtersActive = agentFilter !== ALL_AGENTS || monthFilter || search;

  if (selected) {
    return <TripDetail tripId={selected} onBack={() => { setSelected(null); load(); }} />;
  }

  return (
    <div className="space-y-6" data-testid="trips-panel">
      <Card className="shadow-none border-border">
        <CardHeader className="border-b border-border pb-3">
          <div className="flex items-center gap-2">
            <Plus className="h-4 w-4" />
            <h2 className="font-heading text-base font-bold tracking-tight">Importar viaje desde SOFI</h2>
          </div>
          <p className="text-xs text-muted-foreground">
            Pega el ID o la URL del viaje en SOFI y la URL del schedule en kimkim. Quedará guardado bajo el nombre del cliente para que cualquier agente lo retome.
          </p>
        </CardHeader>
        <CardContent className="pt-4">
          <form onSubmit={importTrip} className="grid grid-cols-1 md:grid-cols-[1fr_2fr_auto] gap-3 items-end">
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">SOFI Trip ID o URL</Label>
              <Input
                data-testid="trip-import-sofi-input"
                value={sofiTripId}
                onChange={(e) => setSofiTripId(e.target.value)}
                placeholder="2271 o https://gestion.viajadverdad.com/trips/form/1/2271"
                className="rounded-md"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">URL kimkim Schedule</Label>
              <Input
                data-testid="trip-import-kimkim-input"
                value={kimkimUrl}
                onChange={(e) => setKimkimUrl(e.target.value)}
                placeholder="https://www.kimkim.com/tp/XXXXXX/schedule"
                className="rounded-md"
              />
            </div>
            <Button
              data-testid="trip-import-button"
              type="submit"
              disabled={importing || !sofiTripId.trim() || !kimkimUrl.trim()}
              className="rounded-md font-semibold"
            >
              {importing ? (
                <span className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Importando…</span>
              ) : (
                <span className="flex items-center gap-2"><Workflow className="h-4 w-4" /> Importar y emparejar</span>
              )}
            </Button>
          </form>
          {importing && (
            <p className="text-xs text-muted-foreground mt-3" data-testid="trip-import-hint">
              Leyendo todas las reservas en SOFI y cruzándolas con la agenda kimkim (15-40 segundos)…
            </p>
          )}
        </CardContent>
      </Card>

      {/* Banner: otro agente está usando el scraper — sólo hay un slot para
          evitar carreras de cookies contra SOFI/kimkim.  Se auto-oculta cuando
          termina. */}
      {scrapeStatus?.busy && (
        <div
          data-testid="scrape-status-banner"
          className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 flex items-start gap-3"
        >
          <Loader2 className="h-4 w-4 mt-0.5 text-amber-700 animate-spin shrink-0" />
          <div className="flex-1 text-sm text-amber-900">
            <p className="font-semibold">
              {scrapeStatus.trip.agent} está importando el viaje SOFI #{scrapeStatus.trip.sofi_trip_id}
              {scrapeStatus.trip.client_name && ` — ${scrapeStatus.trip.client_name}`}
              <span className="ml-2 text-xs font-normal text-amber-700">
                (empezó hace {Math.max(0, Math.floor(scrapeStatus.trip.started_seconds_ago / 60))} min {scrapeStatus.trip.started_seconds_ago % 60}s)
              </span>
            </p>
            <p className="text-xs mt-1 text-amber-800">
              Sólo se puede procesar un viaje a la vez para no pisar la sesión compartida de SOFI/kimkim.
              Puedes lanzar tu importación igualmente: se pondrá en cola y arrancará automáticamente cuando esta termine.
              {scrapeStatus.queued > 0 && (
                <span className="ml-1 font-semibold">Hay {scrapeStatus.queued} importación(es) esperando ya.</span>
              )}
            </p>
          </div>
        </div>
      )}

      <Card className="shadow-none border-border" data-testid="trips-list-card">
        <CardHeader className="border-b border-border pb-3 space-y-3">
          {/* Toggle activos/archivados */}
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="inline-flex rounded-md border border-border p-1">
              <button
                type="button"
                data-testid="trips-tab-active"
                onClick={() => setView("active")}
                className={`flex items-center gap-1.5 px-3 py-1 rounded text-sm transition-colors ${
                  view === "active" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <ListChecks className="h-3.5 w-3.5" />
                Itinerarios emparejados
                <span className={`ml-1 text-[10px] font-semibold rounded-full px-1.5 py-0.5 ${
                  view === "active" ? "bg-primary-foreground/20" : "bg-muted"
                }`}>
                  {activeTrips.length}
                </span>
              </button>
              <button
                type="button"
                data-testid="trips-tab-archived"
                onClick={() => setView("archived")}
                className={`flex items-center gap-1.5 px-3 py-1 rounded text-sm transition-colors ${
                  view === "archived" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <Archive className="h-3.5 w-3.5" />
                Archivados
                <span className={`ml-1 text-[10px] font-semibold rounded-full px-1.5 py-0.5 ${
                  view === "archived" ? "bg-primary-foreground/20" : "bg-muted"
                }`}>
                  {archivedTrips.length}
                </span>
              </button>
            </div>
            <Button data-testid="trips-reload-button" variant="outline" size="sm" onClick={load} disabled={loading} className="rounded-md">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            </Button>
          </div>

          {/* Filtros */}
          <div className="grid grid-cols-1 md:grid-cols-[1fr_220px_180px_auto] gap-2 items-end">
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                <Search className="h-3 w-3" /> Cliente
              </Label>
              <Input
                data-testid="trips-filter-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar por nombre…"
                className="rounded-md h-9"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                <User className="h-3 w-3" /> Agente
              </Label>
              <Select value={agentFilter} onValueChange={setAgentFilter}>
                <SelectTrigger data-testid="trips-filter-agent" className="rounded-md h-9">
                  <SelectValue placeholder="Todos los agentes" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_AGENTS}>Todos los agentes</SelectItem>
                  {allAgents.map((email) => (
                    <SelectItem key={email} value={email}>{email}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                <CalendarRange className="h-3 w-3" /> Mes de llegada
              </Label>
              <Input
                data-testid="trips-filter-month"
                type="month"
                value={monthFilter}
                onChange={(e) => setMonthFilter(e.target.value)}
                className="rounded-md h-9"
              />
            </div>
            {filtersActive ? (
              <Button
                data-testid="trips-filter-clear"
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="rounded-md h-9 text-muted-foreground"
              >
                <X className="h-3.5 w-3.5 mr-1" /> limpiar
              </Button>
            ) : (
              <span className="text-xs text-muted-foreground flex items-center gap-1 h-9">
                <Filter className="h-3 w-3" /> {visibleTrips.length} resultados
              </span>
            )}
          </div>
        </CardHeader>

        <CardContent className="pt-0 divide-y divide-border">
          {!loading && visibleTrips.length === 0 && (
            <p className="text-sm text-muted-foreground py-6" data-testid="trips-empty">
              {filtersActive
                ? "Ningún viaje coincide con los filtros aplicados."
                : view === "archived"
                  ? "Aún no hay viajes archivados. Se archivarán automáticamente cuando termine la fecha del viaje."
                  : "Aún no has importado ningún viaje. Usa el formulario de arriba para empezar."}
            </p>
          )}
          {visibleTrips.map((t) => (
            <div
              key={t.id}
              data-testid={`trip-row-${t.sofi_trip_id}`}
              role="button"
              tabIndex={0}
              onClick={() => setSelected(t.id)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSelected(t.id); }
              }}
              className="w-full text-left py-3 flex flex-wrap items-center gap-4 hover:bg-muted/40 px-2 rounded cursor-pointer focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm flex items-center gap-2 truncate">
                  <User className="h-4 w-4 text-primary shrink-0" />
                  {t.client_name || `(viaje SOFI #${t.sofi_trip_id})`}
                  {t.status === "processing" && (
                    <Badge className="rounded-md bg-amber-100 text-amber-800 hover:bg-amber-100 flex items-center gap-1">
                      <Loader2 className="h-3 w-3 animate-spin" /> procesando…
                    </Badge>
                  )}
                  {t.status === "error" && (
                    <Badge className="rounded-md bg-red-100 text-red-700 hover:bg-red-100">error</Badge>
                  )}
                </p>
                <p className="text-xs text-muted-foreground flex flex-wrap gap-2 items-center mt-1">
                  <CalendarRange className="h-3 w-3" />
                  {t.sofi_header?.start_date || "—"} → {t.sofi_header?.end_date || "—"}
                  <span className="text-muted-foreground/60">·</span>
                  SOFI #{t.sofi_trip_id}
                  <span className="text-muted-foreground/60">·</span>
                  kimkim {t.kimkim_trip_code}
                </p>
                <p className="text-[11px] text-muted-foreground/70 mt-0.5">
                  importado por {t.created_by} · actualizado {new Date(t.updated_at).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" })}
                  {t.updated_by !== t.created_by && ` por ${t.updated_by}`}
                  {t.status === "error" && t.status_message && (
                    <span className="text-red-600"> · {t.status_message}</span>
                  )}
                </p>
              </div>
              <Badge className="rounded-md bg-blue-100 text-blue-800 hover:bg-blue-100">
                {t.sofi_header?.destination || "—"}
              </Badge>
              {t.status === "error" && (
                <Button
                  data-testid={`trip-retry-${t.sofi_trip_id}`}
                  variant="ghost"
                  size="sm"
                  onClick={(e) => { e.stopPropagation(); retryTrip(t); }}
                  className="rounded-md text-blue-700 hover:bg-blue-50"
                  title="Reintentar importación"
                >
                  <RefreshCw className="h-4 w-4" />
                </Button>
              )}
              <Button
                data-testid={`trip-delete-${t.sofi_trip_id}`}
                variant="ghost"
                size="sm"
                onClick={(e) => { e.stopPropagation(); removeTrip(t); }}
                className="rounded-md text-red-600 hover:bg-red-50"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
